import { Box, Grid } from 'vendor/material';
import { useFormikContext } from 'formik';
import {
  AiTextFormField,
  EditModalType,
  MultiSelectFormField,
  RadioCheckboxGroupFormField,
  SingleSelectFormField,
  TextFormField,
} from 'packages/cosell/src/components';

import {
  AcePartnerNeedType,
  AceOpportunityTypeEnum,
  OpportunityIndustryEnum,
  CompetitiveTrackingEnum,
  FeatureFlagState,
  AceOpportunityReviewStatusEnum,
  CountryCodeEnum,
} from 'packages/cosell/src/types/enums';
import OfficeBuilding from 'mdi-material-ui/OfficeBuilding';
import { useAmpliFeatureFlags } from '@tackle-io/tackle-auth-tools';
import { CoSellFeatureFlags } from 'utils/features';
import { UnifiedOpportunityFormSection } from '../UnifiedOpportunityFormSection';
import {
  AssignmentIcon,
  ChecklistIcon,
  PersonIcon,
} from 'packages/cosell/assets';
import {
  CreateAceOpportunityFormValues,
  SolutionsOfferedToggleEnum,
  UpdateAceOpportunityFormValues,
} from './AceOpportunityFormValues';
import {
  getAwsFundingUsedOptions,
  getCoSellStageOptions,
  getNationalSecurityOptions,
  getOpportunitySourceOptions,
  getOpportunityTypeOptions,
  solutionsOfferedToggleOptions,
  handleOpportunitySourceChange,
  handleOpportunityTypeChange,
  handlePartnerNeedTypeChange,
  handleSolutionRelatedFieldsChange,
  isFieldEditable,
  UNITED_STATES_COUNTRY_CODE,
  partnerTypeNeedOptions,
  primaryContactTitleOptions,
} from './helpers';
import { useCallback, useEffect, useState } from 'react';
import { scrollToFirstError } from 'packages/cosell/src/utilities/scroll/scrollToFirstError';
import { isEmpty } from 'lodash';
import { MarketingSourceEnum } from 'packages/cosell/src/types/enums/MarketingSourceEnum';
import {
  AdditionalComments,
  DeliveryModels,
  Solutions,
  TargetCloseDate,
} from './SharedFormFields';
import {
  CurrencyCode,
  CustomerSoftwareValue,
  EffectiveDate,
  ExpirationDate,
  ProcurementType,
  MarketplaceOfferId,
  ReasonForClosing,
} from './EditModalOnlyFields';
import { AwsEnumListResponse } from 'packages/cosell/src/types/responses/AwsEnumResponse';
import { ExpectedCustomerSpendFieldSet } from './ExpectedCustomerSpendFieldSet';
import {
  coSellAceOpportunityFormFieldsDataId,
  coSellCreateEditPageDataId,
} from 'packages/cosell/src/utilities/intercomEnums';
import { useAcePickListQuery } from 'packages/cosell/api/hooks/useAcePickList';
import { useAceVendorConfig } from 'packages/cosell/api/hooks/useAceVendorConfig';
import { useCoSellContext } from 'packages/cosell/src/CoSellContextProvider';
import { Loader } from '@tackle-io/platform-ui';
import { AceNationalSecurityEnum } from 'packages/cosell/src/types/enums/AceNationalSecurityEnum';

const useCoSellFeatureFlags = () => {
  const { loading: ampliloading, flags: ampliflags } = useAmpliFeatureFlags();
  return {
    ampliloading,
    showAiGenerateButton:
      !!ampliflags &&
      ampliflags[CoSellFeatureFlags.BETA_AI_COSELL_DESCRIPTION] ===
        FeatureFlagState.ON
        ? true
        : false,
  };
};

const renderEditModalForm = ({
  editModalType,
  // values,
  status,
  awsEnumListMap,
  // setFieldValue,
  enableSaasDocumentationFields,
}: {
  editModalType: EditModalType;
  values: UpdateAceOpportunityFormValues;
  status: AceOpportunityReviewStatusEnum | null;
  awsEnumListMap: AwsEnumListResponse;
  setFieldValue: (field: string, value: any) => void;
  enableSaasDocumentationFields: boolean;
}) => {
  switch (editModalType) {
    case EditModalType.LAUNCH:
      return (
        <div>
          <Box mb={2}>
            <MarketplaceOfferId status={status} />
          </Box>
          <Box mb={2}>
            <Solutions status={status} />
          </Box>
          <Box mb={2}>
            <TargetCloseDate status={status} />
          </Box>
          <Box mb={2}>
            <ProcurementType
              status={status}
              awsEnumListMap={awsEnumListMap}
              required={enableSaasDocumentationFields}
            />
          </Box>
          <Box mb={2}>
            <CustomerSoftwareValue
              status={status}
              required={enableSaasDocumentationFields}
            />
          </Box>
          <Box mb={2}>
            <CurrencyCode
              awsEnumListMap={awsEnumListMap}
              required={enableSaasDocumentationFields}
            />
          </Box>
          <Box mb={2}>
            <EffectiveDate
              status={status}
              required={enableSaasDocumentationFields}
            />
          </Box>
          <Box mb={2}>
            <ExpirationDate
              status={status}
              required={enableSaasDocumentationFields}
            />
          </Box>
        </div>
      );
    case EditModalType.EDIT_LAUNCHED:
      return (
        <div>
          <Box mb={2}>
            <DeliveryModels awsEnumListMap={awsEnumListMap} />
          </Box>
          <Box mb={2}>
            <AdditionalComments status={status} />
          </Box>
          {enableSaasDocumentationFields && (
            <>
              <Box mb={2}>
                <ProcurementType
                  status={status}
                  awsEnumListMap={awsEnumListMap}
                  required={enableSaasDocumentationFields}
                />
              </Box>
              <Box mb={2}>
                <CustomerSoftwareValue
                  status={status}
                  required={enableSaasDocumentationFields}
                />
              </Box>
              <Box mb={2}>
                <CurrencyCode
                  awsEnumListMap={awsEnumListMap}
                  required={enableSaasDocumentationFields}
                />
              </Box>
              <Box mb={2}>
                <EffectiveDate
                  status={status}
                  required={enableSaasDocumentationFields}
                />
              </Box>
              <Box mb={2}>
                <ExpirationDate
                  status={status}
                  required={enableSaasDocumentationFields}
                />
              </Box>
            </>
          )}
        </div>
      );
    case EditModalType.CLOSE_LOST:
      return (
        <div>
          <Box mb={2}>
            <ReasonForClosing status={status} awsEnumListMap={awsEnumListMap} />
          </Box>
          <Box mb={2}>
            <AdditionalComments status={status} />
          </Box>
        </div>
      );
  }
};

const AceOpportunityFormFields = ({
  editModalType = null,
  reviewStatus,
  isCreatedInCloud,
}: {
  editModalType?: EditModalType | null;
  reviewStatus: AceOpportunityReviewStatusEnum | null;
  isCreatedInCloud?: boolean;
}) => {
  const { values, setFieldValue, errors, isValid, isValidating, submitCount } =
    useFormikContext<
      CreateAceOpportunityFormValues | UpdateAceOpportunityFormValues
    >();
  const { data: awsEnumListMap } = useAcePickListQuery();
  const { isSaasDocumentationRequiredToLaunch } = useAceVendorConfig();
  const { opportunityId } = useCoSellContext();

  /** If the opportunity has not been created in the cloud successfully,
   * we should still be able to edit partner contact fields */
  const shouldDisablePartnerContactFields = !!opportunityId && isCreatedInCloud;

  const { ampliloading, showAiGenerateButton } = useCoSellFeatureFlags();
  const [hasScrolledToFirstError, setHasScrolledToFirstError] = useState(false);
  useEffect(() => {
    const shouldScrollToError =
      !isValid &&
      !isEmpty(errors) &&
      submitCount > 0 &&
      !hasScrolledToFirstError;
    if (!shouldScrollToError) {
      return;
    }

    const hasScrolled = scrollToFirstError();
    setHasScrolledToFirstError(hasScrolled);
  }, [errors, isValidating, isValid, submitCount, hasScrolledToFirstError]);

  useEffect(() => {
    setHasScrolledToFirstError(false);
  }, [submitCount]);

  useEffect(() => {
    const shouldScrollToError = !isValid && !isEmpty(errors) && submitCount > 0;
    if (!shouldScrollToError) {
      return;
    }

    scrollToFirstError();
  }, [errors, isValidating, isValid, submitCount]);

  const isFieldDisabled = useCallback(
    (fieldName: string) => {
      return (
        !!reviewStatus &&
        !isFieldEditable({
          name: fieldName,
          status: reviewStatus,
        })
      );
    },
    [reviewStatus],
  );

  const isOppFromMarketingActivity =
    values?.marketingSource === MarketingSourceEnum.MARKETING_ACTIVITY;

  if (!awsEnumListMap) {
    return <Loader />;
  }

  if (editModalType && !!awsEnumListMap) {
    return renderEditModalForm({
      editModalType,
      values: values as UpdateAceOpportunityFormValues,
      status: reviewStatus,
      awsEnumListMap,
      setFieldValue,
      enableSaasDocumentationFields:
        isSaasDocumentationRequiredToLaunch({
          deliveryModels: values.deliveryModels,
        }) ?? false,
    });
  }

  return (
    <>
      <div>
        {/* Section 1: Customer Details */}
        {reviewStatus === AceOpportunityReviewStatusEnum.APPROVED && (
          <UnifiedOpportunityFormSection
            title="Co-Sell stage"
            titleIcon={<ChecklistIcon />}
            dataId={coSellCreateEditPageDataId.CO_SELL_STAGE_SECTION_HEADER}
          >
            <Grid item xs={12}>
              <SingleSelectFormField
                fieldId="stage"
                filterSelectedOptions={true}
                label="Co-Sell stage"
                options={getCoSellStageOptions(
                  awsEnumListMap?.['lifeCycle.stage'],
                )}
                dataId={coSellAceOpportunityFormFieldsDataId.CO_SELL_STAGE}
                required
                helperText="Share progress with your cloud partner by updating the Co-Sell stage. Please note that co-sells can only be “Launched” or marked “Closed Lost” on the details page."
              />
            </Grid>
          </UnifiedOpportunityFormSection>
        )}

        {/* Section 1: Customer Details */}
        <UnifiedOpportunityFormSection
          title="Customer details"
          titleIcon={<OfficeBuilding />}
          dataId={coSellCreateEditPageDataId.CUSTOMER_DETAILS_SECTION_HEADER}
        >
          <Grid item xs={12}>
            <TextFormField
              name="duns"
              label="Customer data universal number system (DUNS)"
              dataId={coSellAceOpportunityFormFieldsDataId.CUSTOMER_DUNS_NUMBER}
              helperText="Customer DUNS is 9 digits. For example: 123456789"
              disabled={isFieldDisabled('duns')}
            />
          </Grid>
          <Grid item xs={12}>
            <TextFormField
              name="customerCompanyName"
              label="Customer company name"
              dataId={
                coSellAceOpportunityFormFieldsDataId.CUSTOMER_COMPANY_NAME
              }
              disabled={isFieldDisabled('customerCompanyName')}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <SingleSelectFormField
              fieldId="industry"
              label="Industry vertical"
              dataId={coSellAceOpportunityFormFieldsDataId.INDUSTRY}
              options={awsEnumListMap?.['customer.account.industry'] || []}
              onChange={(val) => {
                setFieldValue('industryOther', null);
                if (val === OpportunityIndustryEnum.GOVERNMENT) {
                  // Set a default value before CountryCode requirement is selected
                  setFieldValue('nationalSecurity', AceNationalSecurityEnum.NO);
                } else {
                  setFieldValue('nationalSecurity', null);
                }
              }}
              required
              disabled={isFieldDisabled('industry')}
            />
          </Grid>
          {values.industry === OpportunityIndustryEnum.OTHER ? (
            <Grid item xs={12}>
              <TextFormField
                name="industryOther"
                label="Industry other"
                dataId={coSellAceOpportunityFormFieldsDataId.INDUSTRY_OTHER}
                disabled={isFieldDisabled('industryOther')}
              />
            </Grid>
          ) : null}
          {values.industry === OpportunityIndustryEnum.GOVERNMENT &&
          (!values.countryCode ||
            values.countryCode === UNITED_STATES_COUNTRY_CODE) ? (
            <Grid item xs={12}>
              <RadioCheckboxGroupFormField
                id={coSellAceOpportunityFormFieldsDataId.NATIONAL_SECURITY}
                label="Classified national security information"
                radioOptions={getNationalSecurityOptions(
                  awsEnumListMap?.['nationalSecurity'],
                )}
                helperText="Specify whether this opportunity contains classified National Security information."
                required
                name={'nationalSecurity'}
                disabled={isFieldDisabled('nationalSecurity')}
              />
            </Grid>
          ) : null}
          <Grid item xs={12}>
            <TextFormField
              name="customerWebsite"
              label="Customer website"
              dataId={coSellAceOpportunityFormFieldsDataId.CUSTOMER_WEBSITE}
              disabled={isFieldDisabled('customerWebsite')}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <SingleSelectFormField
              fieldId="countryCode"
              data-testid="countrySingleSelectFormField"
              dataId={coSellAceOpportunityFormFieldsDataId.COUNTRY_CODE}
              label="Country"
              onChange={(val) => {
                if (val !== CountryCodeEnum.UNITED_STATES) {
                  setFieldValue('nationalSecurity', null);
                }
              }}
              options={
                awsEnumListMap?.['customer.account.address.countryCode'] || []
              }
              disabled={isFieldDisabled('countryCode')}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextFormField
              name="postalCode"
              label="Postal code"
              dataId={coSellAceOpportunityFormFieldsDataId.POSTAL_CODE}
              helperText={
                values.countryCode === UNITED_STATES_COUNTRY_CODE
                  ? 'United States postal code must be in 99999 OR 99999-9999 format.'
                  : ''
              }
              disabled={isFieldDisabled('countryCode')}
              required
            />
          </Grid>
          {values.countryCode === UNITED_STATES_COUNTRY_CODE ? (
            <Grid item xs={12}>
              <SingleSelectFormField
                data-testid="stateTextFormField"
                fieldId="state"
                label="State/Province"
                options={
                  awsEnumListMap?.['customer.account.address.stateOrRegion'] ||
                  []
                }
                dataId={coSellAceOpportunityFormFieldsDataId.STATE_PROVINCE}
                disabled={isFieldDisabled('state')}
                required
              />
            </Grid>
          ) : null}
          <Grid item xs={12}>
            <TextFormField
              name="city"
              label="City"
              dataId={coSellAceOpportunityFormFieldsDataId.CITY}
              disabled={isFieldDisabled('city')}
            />
          </Grid>
          <Grid item xs={12}>
            <TextFormField
              name="address"
              label="Address"
              dataId={coSellAceOpportunityFormFieldsDataId.ADDRESS}
              disabled={isFieldDisabled('address')}
            />
          </Grid>
        </UnifiedOpportunityFormSection>

        {/* Section 2: Project details */}
        <UnifiedOpportunityFormSection
          title="Project details"
          titleIcon={<AssignmentIcon />}
          dataId={coSellCreateEditPageDataId.PROJECT_DETAILS_SECTION_HEADER}
        >
          <Grid item xs={12}>
            <RadioCheckboxGroupFormField
              id={coSellAceOpportunityFormFieldsDataId.PARTNER_NEED_TYPE}
              label="Partner primary need from AWS"
              radioOptions={partnerTypeNeedOptions}
              required
              name={'partnerNeedType'}
              onChange={(e) => handlePartnerNeedTypeChange(e, setFieldValue)}
              disabled={isFieldDisabled('partnerNeedType')}
            />
          </Grid>
          {values.partnerNeedType !==
            AcePartnerNeedType.DO_NOT_NEED_SUPPORT_FROM_AWS_SALES_REP && (
            <Grid item xs={12}>
              <MultiSelectFormField
                fieldId={'primaryNeedsFromAws'}
                label={'Partner needs from AWS'}
                dataId={
                  coSellAceOpportunityFormFieldsDataId.PRIMARY_NEEDS_FROM_AWS
                }
                options={awsEnumListMap?.['primaryNeedsFromAws[]'] || []}
                required={
                  values.partnerNeedType !==
                  AcePartnerNeedType.DO_NOT_NEED_SUPPORT_FROM_AWS_SALES_REP
                }
                filterSelectedOptions={true}
                disabled={isFieldDisabled('primaryNeedsFromAws')}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <RadioCheckboxGroupFormField
              name="opportunityType"
              id={coSellAceOpportunityFormFieldsDataId.OPPORTUNITY_TYPE}
              label="Opportunity type"
              radioOptions={getOpportunityTypeOptions(
                awsEnumListMap?.['opportunityType'],
              )}
              onChange={(e) => handleOpportunityTypeChange(e, setFieldValue)}
              required
              disabled={isFieldDisabled('opportunityType')}
            />
          </Grid>
          {values.opportunityType === AceOpportunityTypeEnum.EXPANSION ||
          values.opportunityType === AceOpportunityTypeEnum.FLAT_RENEWAL ? (
            <Grid item xs={12}>
              <TextFormField
                label="Parent co-sell ID"
                dataId={coSellAceOpportunityFormFieldsDataId.PARENT_CO_SELL_ID}
                name="parentOppId"
                disabled={isFieldDisabled('parentOppId')}
              />
            </Grid>
          ) : null}
          <Grid item xs={12}>
            <TextFormField
              name="projectTitle"
              label="Partner project title"
              dataId={coSellAceOpportunityFormFieldsDataId.PROJECT_TITLE}
              multiline={true}
              maxLength={255}
              rows={6}
              required
              disabled={isFieldDisabled('projectTitle')}
            />
          </Grid>
          <Grid item xs={12}>
            <MultiSelectFormField
              fieldId={'salesActivities'}
              label={'Sales activities'}
              dataId={coSellAceOpportunityFormFieldsDataId.SALES_ACTIVITIES}
              options={awsEnumListMap?.['project.salesActivities[]'] || []}
              disabled={isFieldDisabled('salesActivities')}
              // Sales activities is required when the partner needs support from AWS.
              required={
                values.partnerNeedType !==
                AcePartnerNeedType.DO_NOT_NEED_SUPPORT_FROM_AWS_SALES_REP
              }
              helperText="Choose sales activities that have happened with the end-customer."
              filterSelectedOptions={true}
            />
          </Grid>
          <Grid item xs={12}>
            {!ampliloading && showAiGenerateButton ? (
              <AiTextFormField
                name="customerBusinessProblem"
                dependsOnFields={['customerWebsite']}
                label="Customer business problem"
                dataId={
                  coSellAceOpportunityFormFieldsDataId.CUSTOMER_BUSINESS_PROBLEM
                }
                multiline
                maxLength={2000}
                minLength={20}
                // placeholder="Add your own description, or select Generate with Tackle AI to get started"
                required
                helperText="Describe the business problem that most closely aligns to your customer’s use case. Must be at least 20 characters."
                disabled={isFieldDisabled('customerBusinessProblem')}
              />
            ) : (
              <TextFormField
                name="customerBusinessProblem"
                label="Customer business problem"
                dataId={
                  coSellAceOpportunityFormFieldsDataId.CUSTOMER_BUSINESS_PROBLEM
                }
                multiline
                rows={6}
                maxLength={2000}
                minLength={20}
                required
                helperText="Describe the business problem that most closely aligns to your customer’s use case. Must be at least 20 characters."
                disabled={isFieldDisabled('customerBusinessProblem')}
              />
            )}
          </Grid>

          <Grid item xs={12}>
            <RadioCheckboxGroupFormField
              label={'Solutions offered'}
              id={coSellAceOpportunityFormFieldsDataId.SOLUTIONS_OFFERED_TOGGLE}
              name="solutionsOfferedToggle"
              required
              radioOptions={solutionsOfferedToggleOptions}
              onChange={(e) =>
                handleSolutionRelatedFieldsChange(e, setFieldValue)
              }
              disabled={isFieldDisabled('solutionsOfferedToggle')}
            />
          </Grid>

          <Grid item xs={12}>
            {values?.solutionsOfferedToggle ===
            SolutionsOfferedToggleEnum.OTHER_SOLUTION ? (
              <TextFormField
                name="otherSolutionDescription"
                label="Other solution offered"
                dataId={
                  coSellAceOpportunityFormFieldsDataId.OTHER_SOLUTION_OFFERED
                }
                required
                disabled={isFieldDisabled('otherSolutionDescription')}
              />
            ) : (
              <Solutions status={reviewStatus} />
            )}
          </Grid>
          {/* comment out AWS products for now 11/06/2024 */}
          {/* Also add back in for AceProjectDetails.tsx */}
          {/* <Grid item xs={12}>
            <MultiSelectFormField
              fieldId={'awsProducts'}
              label={'AWS products'}
              options={[]}
              disabled={isFieldDisabled('awsProducts')}
              // Sales activities is required when the partner needs support from AWS.
              required={
                false
                // values.partnerNeedType !==
                // AcePartnerNeedType.DO_NOT_NEED_SUPPORT_FROM_AWS_SALES_REP
              }
              helperText="Choose one or more AWS products that will be utilized to solve the customer's business problem. Adding products enables AWS to connect you with the right support on this opportunity."
              filterSelectedOptions={true}
            />
          </Grid> */}
          <Grid item xs={12}>
            <TextFormField
              name="nextStep"
              label="Next step"
              dataId={coSellAceOpportunityFormFieldsDataId.NEXT_STEP}
              rows={6}
              multiline
              placeholder="Describe the next steps for this opportunity"
              maxLength={255}
              disabled={isFieldDisabled('nextStep')}
            />
          </Grid>
          <Grid item xs={12}>
            <SingleSelectFormField
              fieldId="customerUseCase"
              filterSelectedOptions={true}
              label="Use case"
              options={awsEnumListMap?.['project.customerUseCase'] || []}
              required
              dataId={coSellAceOpportunityFormFieldsDataId.CUSTOMER_USE_CASE}
              disabled={isFieldDisabled('customerUseCase')}
            />
          </Grid>
          <Grid item xs={12}>
            <DeliveryModels
              awsEnumListMap={awsEnumListMap}
              disabled={isFieldDisabled('deliveryModels')}
            />
          </Grid>

          <Grid item xs={12}>
            <MultiSelectFormField
              fieldId="apnPrograms"
              label={'APN programs'}
              dataId={coSellAceOpportunityFormFieldsDataId.APN_PROGRAMS}
              options={awsEnumListMap?.['project.apnPrograms[]'] || []}
              filterSelectedOptions={true}
              required={false}
              disabled={isFieldDisabled('apnPrograms')}
            />
          </Grid>
          <Grid item xs={12}>
            <TargetCloseDate status={reviewStatus} />
          </Grid>
          <Grid xs={12} item style={{ marginTop: '24px' }}>
            <ExpectedCustomerSpendFieldSet />
          </Grid>
        </UnifiedOpportunityFormSection>

        {/* Section 3: Marketing details */}
        <UnifiedOpportunityFormSection
          title="Marketing details"
          titleIcon={<AssignmentIcon />}
          dataId={coSellCreateEditPageDataId.MARKETING_DETAILS_SECTION_HEADER}
        >
          <Grid item xs={12}>
            <RadioCheckboxGroupFormField
              label={'Opportunity source'}
              name="marketingSource"
              required
              id={coSellAceOpportunityFormFieldsDataId.OPPORTUNITY_SOURCE}
              radioOptions={getOpportunitySourceOptions(
                awsEnumListMap?.['marketing.source'],
              )}
              onChange={(e) => handleOpportunitySourceChange(e, setFieldValue)}
              disabled={isFieldDisabled('marketingSource')}
            />
          </Grid>

          {isOppFromMarketingActivity && (
            <>
              <Grid item xs={12}>
                <TextFormField
                  name="campaignName"
                  label="Marketing campaign"
                  dataId={
                    coSellAceOpportunityFormFieldsDataId.MARKETING_CAMPAIGN
                  }
                  disabled={isFieldDisabled('campaignName')}
                />
              </Grid>
              <Grid item xs={12}>
                <MultiSelectFormField
                  fieldId={'marketingActivityUseCases'}
                  label={'Marketing activity use case'}
                  dataId={
                    coSellAceOpportunityFormFieldsDataId.MARKETING_ACTIVITY_USE_CASE
                  }
                  options={awsEnumListMap?.['marketing.useCases[]'] || []}
                  disabled={isFieldDisabled('marketingActivityUseCases')}
                  required={false}
                  filterSelectedOptions={true}
                />
              </Grid>
              <Grid item xs={12}>
                <MultiSelectFormField
                  fieldId={'marketingActivityChannel'}
                  label={'Marketing activity channel'}
                  dataId={
                    coSellAceOpportunityFormFieldsDataId.MARKETING_ACTIVITY_CHANNEL
                  }
                  options={awsEnumListMap?.['marketing.channels[]'] || []}
                  disabled={isFieldDisabled('marketingActivityChannel')}
                  required={false}
                  filterSelectedOptions={true}
                />
              </Grid>
              <Grid item xs={12}>
                <RadioCheckboxGroupFormField
                  id={
                    coSellAceOpportunityFormFieldsDataId.MARKETING_DEVELOPMENT_FUNDS
                  }
                  label="Marketing development funds"
                  radioOptions={getAwsFundingUsedOptions(
                    awsEnumListMap?.['marketing.awsFundingUsed'],
                  )}
                  required
                  name={'isMarketingDevelopmentFunded'}
                  disabled={isFieldDisabled('isMarketingDevelopmentFunded')}
                />
              </Grid>
            </>
          )}
        </UnifiedOpportunityFormSection>

        {/* Section 4: Additional Details */}
        <UnifiedOpportunityFormSection
          title="Additional details"
          titleIcon={<AssignmentIcon />}
          dataId={coSellCreateEditPageDataId.ADDITIONAL_DETAILS_SECTION_HEADER}
        >
          <Grid item xs={12}>
            <SingleSelectFormField
              fieldId="competitiveTracking"
              data-testid="competitiveTracking"
              label="Competitive tracking"
              onChange={() => setFieldValue('otherCompetitorNames', null)}
              options={awsEnumListMap?.['project.competitorName'] || []}
              dataId={coSellAceOpportunityFormFieldsDataId.COMPETITIVE_TRACKING}
              disabled={isFieldDisabled('competitiveTracking')}
            />
          </Grid>
          {values.competitiveTracking === CompetitiveTrackingEnum.OTHER ? (
            <Grid item xs={12}>
              <TextFormField
                name="otherCompetitorNames"
                label="Other competitors"
                dataId={coSellAceOpportunityFormFieldsDataId.OTHER_COMPETITORS}
                disabled={isFieldDisabled('otherCompetitorNames')}
              />
            </Grid>
          ) : null}
          <Grid item xs={12}>
            <TextFormField
              name="awsAccountId"
              label="AWS account ID"
              dataId={coSellAceOpportunityFormFieldsDataId.AWS_ACCOUNT_ID}
              helperText="12 digit number. For example: 123456789012"
              disabled={isFieldDisabled('awsAccountId')}
            />
          </Grid>
          <Grid item xs={12}>
            <AdditionalComments status={reviewStatus} />
          </Grid>
        </UnifiedOpportunityFormSection>
        {/* Section 5: Customer contact */}
        <UnifiedOpportunityFormSection
          title="Customer contact"
          titleIcon={<PersonIcon />}
          dataId={coSellCreateEditPageDataId.CUSTOMER_CONTACT_SECTION_HEADER}
        >
          <Grid item xs={12}>
            <TextFormField
              name="customerFirstName"
              label="Customer first name"
              dataId={coSellAceOpportunityFormFieldsDataId.CUSTOMER_FIRST_NAME}
              disabled={isFieldDisabled('customerFirstName')}
            />
          </Grid>
          <Grid item xs={12}>
            <TextFormField
              name="customerLastName"
              label="Customer last name"
              dataId={coSellAceOpportunityFormFieldsDataId.CUSTOMER_LAST_NAME}
              disabled={isFieldDisabled('customerLastName')}
            />
          </Grid>
          <Grid item xs={12}>
            <TextFormField
              name="customerTitle"
              label="Customer title"
              dataId={coSellAceOpportunityFormFieldsDataId.CUSTOMER_TITLE}
              disabled={isFieldDisabled('customerTitle')}
            />
          </Grid>
          <Grid item xs={12}>
            <TextFormField
              name="customerEmail"
              label="Customer email"
              dataId={coSellAceOpportunityFormFieldsDataId.CUSTOMER_EMAIL}
              disabled={isFieldDisabled('customerEmail')}
            />
          </Grid>
          <Grid item xs={12}>
            <TextFormField
              name="customerPhone"
              label="Customer phone"
              dataId={coSellAceOpportunityFormFieldsDataId.CUSTOMER_PHONE}
              disabled={isFieldDisabled('customerPhone')}
            />
          </Grid>
        </UnifiedOpportunityFormSection>

        {/* Section 6: Partner contact */}
        <UnifiedOpportunityFormSection
          title="Partner contact"
          titleIcon={<PersonIcon />}
          tooltip="AWS sales team may reach out to this contact in regard to the opportunity."
          dataId={coSellCreateEditPageDataId.PARTNER_CONTACT_SECTION_HEADER}
        >
          <Grid item xs={12}>
            <TextFormField
              name="primaryContactFirstName"
              label="Primary contact first name"
              dataId={
                coSellAceOpportunityFormFieldsDataId.PRIMARY_CONTACT_FIRST_NAME
              }
              disabled={shouldDisablePartnerContactFields}
            />
          </Grid>
          <Grid item xs={12}>
            <TextFormField
              name="primaryContactLastName"
              label="Primary contact last name"
              dataId={
                coSellAceOpportunityFormFieldsDataId.PRIMARY_CONTACT_LAST_NAME
              }
              disabled={shouldDisablePartnerContactFields}
            />
          </Grid>
          <Grid item xs={12}>
            {/* AWS currently only accepts PartnerAccountManager as the businessTitle for partnerOpportunityTeam members.
              [Member must satisfy enum value set: [PartnerAccountManager]]*/}
            <SingleSelectFormField
              fieldId="primaryContactTitle"
              data-testid="primaryContactTitle"
              label="Primary contact title"
              options={primaryContactTitleOptions}
              dataId={
                coSellAceOpportunityFormFieldsDataId.PRIMARY_CONTACT_TITLE
              }
              disabled={shouldDisablePartnerContactFields}
            />
            {/* <TextFormField
              name="primaryContactTitle"
              label="Primary contact title"
              dataId={
                coSellAceOpportunityFormFieldsDataId.PRIMARY_CONTACT_TITLE
              }
              disabled={isFieldDisabled('primaryContactTitle') }
            /> */}
          </Grid>
          <Grid item xs={12}>
            <TextFormField
              name="primaryContactEmail"
              label="Primary contact email"
              dataId={
                coSellAceOpportunityFormFieldsDataId.PRIMARY_CONTACT_EMAIL
              }
              disabled={shouldDisablePartnerContactFields}
            />
          </Grid>
          <Grid item xs={12}>
            <TextFormField
              name="primaryContactPhone"
              label="Primary contact phone"
              dataId={
                coSellAceOpportunityFormFieldsDataId.PRIMARY_CONTACT_PHONE
              }
              disabled={shouldDisablePartnerContactFields}
            />
          </Grid>
        </UnifiedOpportunityFormSection>
      </div>
    </>
  );
};

export default AceOpportunityFormFields;
